import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Target, Crosshair, Users, LogIn, Menu, X, User, LogOut, Network, CheckCircle2, ShieldAlert, Layers, UsersRound } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, logout, user, canViewOkr } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: "/okrs", label: "OKRs", icon: Crosshair },
    { path: "/ppp", label: "PPP", icon: Layers },
    { path: "/priorities", label: "Deliverables", icon: Target },
    { path: "/people", label: "People", icon: UsersRound },
    { path: "/my-onething", label: "My OneThing", icon: CheckCircle2 },
    { path: "/my-team", label: "My Team", icon: Users },
    { path: "/org-chart", label: "Org Chart", icon: Network },
    { path: "/accountability", label: "Accountability", icon: ShieldAlert },
    { path: "/admin", label: "Admin", icon: LayoutDashboard },
  ];

  // These nav paths are admin-only — hidden entirely for non-admins, same as the route guard.
  const ADMIN_ONLY_PATHS = ["/admin", "/accountability"];
  const isNavItemVisible = (path: string) =>
    path === "/okrs"
      ? canViewOkr
      : !ADMIN_ONLY_PATHS.includes(path) || user?.role?.name === "admin";

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/infina-logo.png"
            alt="Infina1t Logo"
            className="h-9 w-auto object-contain transition-transform duration-300 hover:scale-110"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-1 md:flex">
          {isAuthenticated && navItems
            .filter(item => isNavItemVisible(item.path))
            .map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive(item.path) ? "secondary" : "ghost"}
                  size="sm"
                  className={cn(
                    "gap-2",
                    isActive(item.path) && "bg-primary/10 text-primary"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Button>
              </Link>
            ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <UserAvatar
                    src={user?.avatarUrl}
                    name={user?.fullName}
                    className="h-9 w-9"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none truncate">{user?.fullName ?? "My Account"}</p>
                    <p className="text-xs leading-none text-muted-foreground truncate">
                      {user?.email ?? "Manage your profile"}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth">
              <Button variant="gradient" size="sm" className="gap-2">
                <LogIn className="h-4 w-4" />
                Sign In
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="border-t border-border bg-background p-4 md:hidden">
          <div className="flex flex-col gap-2">
            {isAuthenticated && navItems
              .filter(item => isNavItemVisible(item.path))
              .map((item) => (
                <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}>
                  <Button
                    variant={isActive(item.path) ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-2",
                      isActive(item.path) && "bg-primary/10 text-primary"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            <div className="my-2 border-t border-border" />

            {isAuthenticated ? (
              <>
                <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => { navigate("/profile"); setIsOpen(false); }}>
                  <User className="h-4 w-4" />
                  Profile
                </Button>
                <Button variant="outline" className="w-full gap-2" onClick={handleLogout}>
                  Sign Out
                </Button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setIsOpen(false)}>
                <Button variant="gradient" className="w-full gap-2">
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
